import React, { useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import { Button, DatePicker, Input, TablePaginationConfig } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import locale from 'antd/es/date-picker/locale/th_TH';
import { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { isFullAccess } from '@/utils/permissionUtils';
import { AxiosResponse } from 'axios';
import { searchBillingNotes } from '@/adapters/billing-note';
import {
  ISearchBillingNoteReq,
  ISearchBillingNoteRes,
  IBillingNote,
} from '@/interfaces/billing-note';
import BillingNoteTable from './components/billing-note-table';
import { useAuth } from '@/contexts/AuthContext';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';

const { Search } = Input;

const _criteria: ISearchBillingNoteReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const BillingNote = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const [criteria, setCriteria] = useState<ISearchBillingNoteReq>({
    ..._criteria,
    businessUnitId: userInfo?.bu || undefined,
  });

  const { data, refetch, isLoading, isFetching } = useQuery<
    AxiosResponse<ISearchBillingNoteRes>,
    Error
  >({
    queryKey: ['search-billing-notes', criteria],
    queryFn: async () => await searchBillingNotes(criteria),
  });

  const onActionBillingNote = () => {
    refetch();
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IBillingNote> | SorterResult<IBillingNote>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof IBillingNote) || newCriteria.orderBy;
      newCriteria.orderType = sorter.order || newCriteria.orderType;
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      orderBy: 'id',
      orderType: 'descend',
      pageStart: 1,
      pageSize: 10,
    });
  };

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: 'descend',
        pageStart: 1,
        pageSize: 10,
        startDate: dates[0]?.toDate(),
        endDate: dates[1]?.toDate(),
      });
    } else {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: 'descend',
        pageStart: 1,
        pageSize: 10,
        startDate: null,
        endDate: null,
      });
    }
  };

  const perFullAccess = isFullAccess();

  return (
    <Card>
      <Title title="ใบวางบิล">
        <div className="flex justify-end gap-3">
          {perFullAccess && (
            <Button
              type="primary"
              onClick={() => navigate(`/billing-note/create`)}
            >
              <PlusOutlined /> เพิ่มใหม่
            </Button>
          )}
        </div>
      </Title>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row justify-between gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <BusinessUnitSelector
              handleSelectData={function (id: number): void {
                setCriteria({ ...criteria, businessUnitId: id });
              }}
              disabled={!!userInfo?.bu}
              value={criteria?.businessUnitId}
            />
            <DatePicker.RangePicker
              size="large"
              locale={locale}
              onChange={onRangeChange}
            />
            <Search
              className="md:max-w-[400px] md:min-w-[400px]"
              placeholder="ค้นหา"
              allowClear
              size="large"
              enterButton="ค้นหา"
              onSearch={onSearch}
            />
          </div>
        </div>
        <BillingNoteTable
          criteria={criteria}
          billingNotes={data?.data || { data: [], totalRecords: 0 }}
          loading={isLoading || isFetching}
          onDelete={onActionBillingNote}
          onTableChange={onTableChange}
        />
      </div>
    </Card>
  );
};

export default BillingNote;
